import { useConnection } from "@solana/wallet-adapter-react";
import type { OpenOrdersResponse, UnsettledBalance } from "@stepfinance/dex";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { settleAllFundsTxs } from "../../../actions";
import { TokenChangeNotificationDisplay } from "../../../components/Notifications/TokenChangeNotificationDisplay";
import { confirmOptions } from "../../../constants/transaction";
import { useLanguageContext } from "../../../contexts/language/language";
import { useConnectedWallet } from "../../../hooks";
import { useOpenbookOrdersQuery } from "../../../hooks/queries/dex/useOpenbookOrdersQuery";
import { notify } from "../../../utils/notifications";
import { handlePreSendTxnError, sendAllTransactions, sendTransaction } from "../../../utils/transactions";

export const useSettleOpenbookFunds = () => {
  const queryClient = useQueryClient();
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { data } = useOpenbookOrdersQuery();
  const {
    currentLanguageData: {
      common,
      pages: {
        swapLP: { notifications: swapNotifications },
      },
    },
  } = useLanguageContext();

  return useMutation({
    mutationFn: async (unsettledBalances: UnsettledBalance[]) => {
      if (!wallet || !data) {
        return;
      }
      const settleArrays = await settleAllFundsTxs({
        connection,
        unsettledBalances: unsettledBalances,
        marketsMap: data.marketsMap,
        openOrdersMap: data.openOrdersMap,
      });
      if (!settleArrays) {
        return;
      }
      const { txs, signers } = settleArrays;
      if (txs.length > 1) {
        return await sendAllTransactions(
          connection,
          wallet,
          txs,
          signers,
          {
            txnPendingMessage: "Your balances are being settled",
          },
          confirmOptions,
          { ...common.notifications, ...swapNotifications },
        );
      } else {
        return await sendTransaction(connection, wallet, txs[0].instructions, signers[0]);
      }
    },
    onSuccess: (txs, settledBalances) => {
      const key = ["openbookOrders", wallet?.publicKey.toBase58()];
      queryClient.setQueryData<OpenOrdersResponse | undefined>(key, (existing) => {
        if (!existing) {
          return;
        }
        const remainingUbs = existing.unsettledBalances.filter(
          (unsettled) => !settledBalances.some((ub) => ub.marketAddress.equals(unsettled.marketAddress)),
        );
        return { ...existing, unsettledBalances: remainingUbs };
      });
      const notificationRows: JSX.Element[] = [];
      settledBalances.forEach((ub) => {
        [ub.baseAmountFree, ub.quoteAmountFree].forEach((amount, index) => {
          const isBaseToken = index === 0;
          if (amount > 0) {
            notificationRows.push(
              <TokenChangeNotificationDisplay
                key={`${ub.marketName} + ${amount}`}
                amount={amount}
                mintAddress={isBaseToken ? ub.baseMint : ub.quoteMint}
                type="increase"
              />,
            );
          }
        });
      });
      if (typeof txs === "string") {
        notify({
          message: "Funds settled",
          description: (
            <>
              <span>You reclaimed:</span>
              {notificationRows.slice(0, 3)}
              {notificationRows.length > 3 && <span>and {notificationRows.length - 3} more balances.</span>}
            </>
          ),
          type: "success",
          txId: txs,
        });
      }
    },
    onError: (error: Error) => handlePreSendTxnError(error, "Error settling balances"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["openbookOrders", wallet?.publicKey.toBase58()] }),
  });
};
